import React, { useRef } from "react";
import { toast } from "sonner";
import { Camera, Pencil, RotateCcw, Gauge, CalendarClock, Building2, Landmark, Fuel, Car } from "lucide-react";
import { api, errorMessage, vehiclePhotoUrl } from "../../lib/api";
import type { Vehicle } from "../../lib/types";
import { daysLabel, CATEGORY_LABEL, fmtDate } from "../../lib/utils";
import { Button } from "../core/Button";
import { BucketBadge, Plate, StatusBadge } from "../core/Badges";

interface Props { v: Vehicle; isBO: boolean; onEdit: () => void; onRestore: () => void; onChanged: () => void }

function Stat({ icon: Icon, label, value, testId }: { icon: typeof Gauge; label: string; value: React.ReactNode; testId: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg bg-slate-50 px-3 py-2" data-testid={testId}>
      <Icon className="h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="truncate text-sm font-semibold text-slate-800">{value}</p></div>
    </div>
  );
}

export function VehicleHero({ v, isBO, onEdit, onRestore, onChanged }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const nextRec = (v.compliance || []).filter((c) => c.days_left != null).sort((a, b) => (a.days_left ?? 0) - (b.days_left ?? 0))[0];
  const upload = async (f: File) => {
    const fd = new FormData();
    fd.append("file", f);
    try { await api.post(`/vehicles/${v.id}/profile-photo`, fd); toast.success("Profile photo updated"); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <section className="card-crisp mb-5 overflow-hidden rise" data-testid="vehicle-hero">
      <div className="grid gap-0 lg:grid-cols-[300px_minmax(0,1fr)]">
        <div className="group relative aspect-[4/3] bg-slate-900 lg:aspect-auto lg:min-h-[220px]">
          {v.profile_file_id ? (
            <img src={vehiclePhotoUrl(v.id, v.profile_file_id)} alt={v.plate_number} className="h-full w-full object-cover" data-testid="vehicle-profile-photo" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center text-slate-500" data-testid="vehicle-profile-placeholder"><Car className="h-12 w-12" strokeWidth={1.25} /><p className="mt-2 text-xs">No profile photo</p></div>
          )}
          {!v.is_deleted && (
            <>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="profile-photo-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-profile-photo-button" className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-md transition-transform hover:scale-105"><Camera className="h-3.5 w-3.5" />{v.profile_file_id ? "Change photo" : "Add photo"}</button>
            </>
          )}
        </div>
        <div className="flex flex-col p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{isBO ? v.tenant_name : "Vehicle record"} · {CATEGORY_LABEL[v.category]} · {v.sub_category || "—"}</p>
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <Plate plate={v.plate_number} className="px-3 py-1 text-lg" />
                <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="vehicle-title">{`${v.make || ""} ${v.model || ""}`.trim() || v.plate_number}</h1>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge status={v.is_deleted ? "archived" : v.status} testId="vehicle-status-badge" />
                <BucketBadge bucket={v.compliance_status} testId="vehicle-compliance-badge" />
                {v.year && <span className="text-xs text-slate-500">{v.year}{v.color ? ` · ${v.color}` : ""}</span>}
                {v.is_deleted && <span className="text-xs text-rose-700">Archived {fmtDate(v.deleted_at)} — {v.deleted_reason}</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!v.is_deleted && <Button variant="secondary" onClick={onEdit} data-testid="edit-vehicle-button"><Pencil className="h-3.5 w-3.5" />Edit</Button>}
              {v.is_deleted && isBO && <Button onClick={onRestore} data-testid="restore-vehicle-button"><RotateCcw className="h-3.5 w-3.5" />Restore</Button>}
            </div>
          </div>
          <div className="mt-auto grid grid-cols-2 gap-2 pt-5 sm:grid-cols-3 xl:grid-cols-5">
            <Stat icon={Gauge} label="Odometer" value={v.odometer_km != null ? `${v.odometer_km.toLocaleString()} km` : "—"} testId="hero-odometer" />
            <Stat icon={CalendarClock} label="Next expiry" value={nextRec ? <span className={nextRec.days_left! < 0 ? "text-rose-700" : nextRec.days_left! <= 30 ? "text-amber-800" : ""}>{nextRec.type_label?.split(" (")[0]} · {daysLabel(nextRec.days_left)}</span> : "—"} testId="hero-next-expiry" />
            <Stat icon={Landmark} label="Department" value={v.department_name || "Unassigned"} testId="hero-department" />
            <Stat icon={Fuel} label="Fuel" value={v.fuel_type || "—"} testId="hero-fuel" />
            <Stat icon={Building2} label="Telematics" value={v.geotab_device_id ? <span className="font-mono text-xs">{v.geotab_device_id}</span> : <span className="text-slate-400">Not linked</span>} testId="hero-telematics" />
          </div>
        </div>
      </div>
    </section>
  );
}
